import { useEffect, useRef, useState } from 'react';
import { translatePhoto, type PhotoTranslateResult } from '../api.js';
import { useSessionStore } from '../state/session.js';

/**
 * 資料モード(写真翻訳)のオーバーレイ。
 *
 * 講義中に配布資料・設計書・画面を撮影すると、専門用語辞書を通した対訳が表示される。
 * 講義本編のセッションと録音は裏で継続しており、下部のミニ字幕バーに直近の字幕を流す
 * (資料を見ている間も講義に置いていかれないため)。結果は講義メモへ自動保存される。
 */

type PhotoPhase = 'picking' | 'translating' | 'done' | 'error';

export function PhotoMode({
  clientSessionId,
  device,
  onClose,
}: {
  clientSessionId: string;
  device: { deviceId?: string; deviceName?: string };
  onClose: () => void;
}): JSX.Element {
  const [phase, setPhase] = useState<PhotoPhase>('picking');
  const [preview, setPreview] = useState<string>();
  const [result, setResult] = useState<PhotoTranslateResult>();
  const [error, setError] = useState<string>();
  const [zoomed, setZoomed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 直近の字幕4行(確定+進行中)。本編セッションは裏で動き続けている。
  const segments = useSessionStore((s) => s.segments);
  const ticker = segments.filter((s) => s.translatedJa !== '').slice(-4);

  useEffect(() => {
    // 開いたら即カメラを起こす(ワンタップ節約)。キャンセル時は picking のまま。
    inputRef.current?.click();
  }, []);

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setPhase('translating');
    setError(undefined);
    try {
      const imageBase64 = await downscaleToJpegBase64(file, 1600);
      setPreview(`data:image/jpeg;base64,${imageBase64}`);
      const res = await translatePhoto({
        imageBase64,
        clientSessionId,
        deviceId: device.deviceId,
        deviceName: device.deviceName,
      });
      setResult(res);
      setPhase('done');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '翻訳に失敗しました。');
      setPhase('error');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'var(--bg, #0d1117)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      <div className="row" style={{ alignItems: 'center', padding: '12px 14px 6px' }}>
        <strong style={{ fontSize: 15 }}>📷 資料の翻訳</strong>
        <div className="spacer" />
        <button
          style={{ minHeight: 40, fontSize: 13 }}
          onClick={() => inputRef.current?.click()}
          disabled={phase === 'translating'}
        >
          撮り直す
        </button>
        <button className="primary" style={{ minHeight: 40, fontSize: 13 }} onClick={onClose}>
          字幕に戻る
        </button>
      </div>

      {preview && (
        <img
          src={preview}
          alt="撮影した資料"
          onClick={() => setZoomed(true)}
          style={{
            margin: '4px 14px',
            maxHeight: zoomed ? undefined : 110,
            objectFit: 'contain',
            borderRadius: 8,
            cursor: 'zoom-in',
          }}
        />
      )}
      {zoomed && preview && (
        <div
          onClick={() => setZoomed(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0,0,0,.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <img src={preview} alt="撮影した資料(拡大)" style={{ maxWidth: '100%', maxHeight: '100%' }} />
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 14px' }}>
        {phase === 'picking' && (
          <div className="empty" style={{ paddingTop: 40 }}>
            資料・設計書・画面を撮影してください。
            <br />
            <button className="primary" style={{ marginTop: 14 }} onClick={() => inputRef.current?.click()}>
              📷 撮影する
            </button>
          </div>
        )}
        {phase === 'translating' && (
          <div className="empty" style={{ paddingTop: 40 }}>
            読み取って翻訳しています…(10〜30秒)
          </div>
        )}
        {phase === 'error' && (
          <div className="notice error" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}
        {phase === 'done' &&
          result?.pairs.map((pair, i) => (
            <div key={i} style={{ margin: '7px 0' }}>
              <div style={{ fontSize: 12, color: 'var(--fg-dim, #8b949e)', whiteSpace: 'pre-wrap' }}>
                {pair.zh}
              </div>
              <div style={{ fontSize: 15, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{pair.ja}</div>
            </div>
          ))}
        {phase === 'done' && result?.lectureId && (
          <p className="hint" style={{ marginTop: 12, color: 'var(--ok, #57ab5a)' }}>
            ✓ 講義メモに保存しました
          </p>
        )}
      </div>

      <div
        onClick={onClose}
        style={{
          borderTop: '2px solid var(--ok, #1f6f43)',
          padding: '7px 14px 9px',
          cursor: 'pointer',
          background: 'rgba(127,127,127,0.06)',
        }}
      >
        <div className="hint" style={{ fontSize: 11, marginBottom: 3 }}>
          🎤 講義は継続中(タップで字幕全画面へ)
        </div>
        {ticker.length === 0 ? (
          <div className="hint" style={{ fontSize: 13 }}>(字幕待ち)</div>
        ) : (
          ticker.map((s, i) => (
            <div
              key={s.segmentId}
              style={{
                fontSize: 13,
                lineHeight: 1.4,
                opacity: 0.45 + (i / Math.max(1, ticker.length - 1)) * 0.55,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {s.translatedJa}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** 撮影画像を最大辺 maxPx に縮小して JPEG base64(データURLなし)にする。 */
async function downscaleToJpegBase64(file: File, maxPx: number): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('画像の変換に失敗しました。');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}
